import { maybe, rd, RemoteData } from '@passionware/monads';
import { Capability, FeatureFlag, IntegrationID } from '__types__';
import type { ResourceType } from '__types__/v1/models/ResourceType';
import useModal from 'hooks/useModal';
import AuthService from 'lib/auth/AuthService';
import Env from 'lib/env/Env';
import { enterpriseSidebarShowAll } from 'v5/features/core/enterprise/enterpriseSidebarShowAll';
import {
  BarChart3,
  BookOpenCheck,
  Building2,
  Calendar,
  Camera,
  FileText,
  GalleryHorizontal,
  Home,
  List,
  Megaphone,
  Plus,
  PlusIcon,
  Search,
  Stamp,
  Users
} from 'lucide-react';
import { SplashlightDocuSignModal } from 'modals';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectActiveAccount } from 'store/accounts';
import { selectIntegrationById } from 'store/integrations';
import type { RootStore } from 'store/reducer';
import {
  selectBrandImage,
  getPublicCompanyName
} from 'store/v1/auth/auth.selectors';
import { getBookingTypes } from 'store/v1/bookings/bookings.selectors';
import { openModal as dispatchOpenModal } from 'store/v1/ui/ui.actions';
import { RESOURCE_TYPE_ICONS } from 'v1/helpers/iconSets';
import { withinApp } from 'v4/core/appRoutes';
import {
  useCurrentResourceId,
  useCurrentResourceTypeId
} from 'v4/entities/common/hooks/useCurrentResourceId';
import { useRouteMatch } from 'react-router-dom';
import { myRouting } from 'v5/features/core/routing';
import { KeyboardShortcut } from 'v5/patterns/widgets/KeyboardShortcut';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { Kbd } from 'v5/design-system/Kbd';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { useManageEnterpriseFeature } from 'v5/features/core/enterprise/useManageEnterpriseFeature';
import { appRouting } from 'v5/core/routing/appRouting';
import { getProductionTypesList } from 'store/v1/production_types/production_types.selectors';
import { EnterpriseMainSidebarHeaderSkeleton } from 'v5/features/core/enterprise/EnterpriseMainSidebarHeaderSkeleton';
import { EnterpriseTeamsNavSection } from 'v5/features/core/enterprise/EnterpriseTeamsNavSection';
import { resolveActiveEnterpriseWorkspace } from 'v5/features/core/enterprise/resolveActiveEnterpriseWorkspace';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { EnterpriseWorkspaceSwitcher } from 'v5/features/core/enterprise/EnterpriseWorkspaceSwitcher';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import { MenuCollapsible, MenuItem } from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarCollapse,
  SidebarContent,
  SidebarGroup,
  SidebarHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { cn } from 'v5/platform/dom/cn';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithRoutingService } from 'v5/services/front/RoutingService/RoutingService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import { WithThemeService } from 'v5/services/front/ThemeService/ThemeService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import type { GlobalSearchService } from 'v5/services/GlobalSearchService/GlobalSearchService';
import { useResource } from '../productions/booking-costs/booking/useBookingEntity';
export interface WithGlobalSearchService {
  globalSearchService: GlobalSearchService;
}

export interface EnterpriseMainSidebarProps
  extends WithServices<
    [
      WithRoutingService,
      WithFeatureService,
      WithGlobalSearchService,
      WithSupportService,
      WithThemeService,
      WithOnlineStatusService,
      WithUserPreferencesService,
      WithEnterpriseService,
      WithUserFlowService,
      WithAppScopeService,
      WithResourceTypeService
    ]
  > {
  className?: string;
}

// Local component for menu items with create action button
interface MenuItemWithCreateButtonProps {
  to: string;
  icon?: React.ReactNode;
  level?: number;
  onCreateClick?: (e: React.MouseEvent) => void;
  createTooltip: string;
  children: React.ReactNode;
  className?: string;
  isActiveOverride?: boolean;
  createButtonWrapper?: (node: React.ReactNode) => React.ReactNode;
  autoSelect?: boolean;
}

const MenuItemWithCreateButton: React.FC<MenuItemWithCreateButtonProps> = ({
  to,
  icon,
  level = 0,
  onCreateClick,
  createTooltip,
  children,
  className,
  isActiveOverride,
  createButtonWrapper,
  autoSelect
}) => {
  const createButton = (
    <div
      role="button"
      className={cn(
        'absolute right-1.5 top-1.5 size-7 flex items-center justify-center group-hover/menu-item:opacity-100 opacity-0 transition-all',
        'rounded bg-transparent! backdrop-blur-3xl hover:bg-bg-emphasis/5!'
      )}
      onClick={onCreateClick}
    >
      <PlusIcon className="size-4 shrink-0" />
    </div>
  );

  const createButtonWithTooltip = createButtonWrapper
    ? createButtonWrapper(createButton)
    : createButton;

  return (
    <NavLink to={to} isActiveOverride={isActiveOverride}>
      <MenuItem
        className={cn('group/menu-item relative', className)}
        level={level}
        icon={icon}
        autoSelect={autoSelect}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            {/* empty div solves problem with dynamic create button not always passing refs and props correctly to the trigger */}
            <div className="">{createButtonWithTooltip}</div>
          </TooltipTrigger>
          <TooltipContent side="right">{createTooltip}</TooltipContent>
        </Tooltip>
        {children}
      </MenuItem>
    </NavLink>
  );
};

interface ResourceTypeMenuItemProps {
  resourceType: ResourceType;
  href: string;
  onCreateClick: (resourceTypeId: number) => void;
  activeContactId: number | null;
}

const ResourceTypeMenuItem: React.FC<ResourceTypeMenuItemProps> = ({
  resourceType,
  href,
  onCreateClick,
  activeContactId
}) => {
  const contactResourceTypeId = useResource(activeContactId)?.resource_type_id;
  const activeResourceTypeId = useCurrentResourceTypeId();

  const isActiveFromContact = maybe.mapOrElse(
    contactResourceTypeId,
    resourceTypeId => resourceTypeId === resourceType.id,
    false
  );
  const isActiveFromRoute = activeResourceTypeId === resourceType.id;

  const resourceName = resourceType.name ?? 'Resource';
  const createTooltip = `Create new ${resourceName.toLowerCase()}`;
  const handleCreateClick = React.useCallback(() => {
    onCreateClick(resourceType.id);
  }, [onCreateClick, resourceType.id]);

  return (
    <MenuItemWithCreateButton
      level={1}
      to={href}
      icon={
        <>
          <img
            src={`/images/${RESOURCE_TYPE_ICONS[resourceType.icon]?.black}`}
            alt={resourceName}
            className="size-4 opacity-80 dark:hidden"
          />
          <img
            src={`/images/${RESOURCE_TYPE_ICONS[resourceType.icon]?.white}`}
            alt={resourceName}
            className="size-4 opacity-80 not-dark:hidden"
          />
        </>
      }
      onCreateClick={handleCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={
        isActiveFromContact || isActiveFromRoute ? true : undefined
      }
    >
      {resourceName}
    </MenuItemWithCreateButton>
  );
};

interface BookingTypeMenuItemProps {
  bookingType: { id: number; name: string };
  href: string;
  typeMatchPath: string;
  onCreateClick: () => void;
}

const BookingTypeMenuItem: React.FC<BookingTypeMenuItemProps> = ({
  bookingType,
  href,
  typeMatchPath,
  onCreateClick
}) => {
  const bookingTypeName = bookingType.name || 'Booking';
  const createTooltip = `Create new ${bookingTypeName.toLowerCase()}`;

  const isOnBookingTypePage = useRouteMatch({
    path: typeMatchPath,
    exact: true
  });

  return (
    <MenuItemWithCreateButton
      level={1}
      to={href}
      icon={<Stamp className="size-4" />}
      onCreateClick={onCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={!!isOnBookingTypePage}
    >
      {bookingTypeName}
    </MenuItemWithCreateButton>
  );
};

export function EnterpriseMainSidebar({
  services,
  className
}: EnterpriseMainSidebarProps) {
  const can = enterpriseSidebarShowAll;
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const scopeRd = services.appScopeService.useAppScope();
  const scopeRdStable = rd.useLastWithPlaceholder(scopeRd);
  const scope = rd.tryGet(scopeRdStable);
  const organizationId = scope?.organizationId ?? '';
  const history = useHistory();
  const orgRouting = appRouting.org(organizationId);

  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId ? maybe.of(organizationId) : maybe.ofAbsent()
  );
  const workspacesRdStable = rd.useLastWithPlaceholder(workspacesRd);
  const headerReady =
    rd.isSuccess(workspacesRdStable) ||
    rd.isPlaceholderData(workspacesRdStable);
  const activeEnterpriseRd = rd.useMemoMap(
    rd.useCombine({
      workspaces: workspacesRdStable,
      scope: scopeRdStable
    }),
    ({ workspaces, scope }) =>
      resolveActiveEnterpriseWorkspace(workspaces, scope)
  );
  const { activeWorkspace, activeWorkspaceSlug, activeWorkspaceId } =
    rd.getOrElse(activeEnterpriseRd, {
      activeWorkspace: undefined,
      activeWorkspaceSlug: '',
      activeWorkspaceId: undefined,
      browsingWorkspaceScope: null
    });
  const scopeWorkspaceNumericId =
    scope?.level === 'workspace' || scope?.level === 'team'
      ? scope.workspaceId
      : activeWorkspaceId;
  const scopeWorkspaceSlug =
    scope?.level === 'workspace' || scope?.level === 'team'
      ? scope.workspaceSlug
      : activeWorkspaceSlug;
  const workspaceLinks = useMemo(
    () => orgRouting.workspace(scopeWorkspaceSlug),
    [orgRouting, scopeWorkspaceSlug]
  );
  const { resources, callouts, bookings, calendar } = workspaceLinks;

  const teamsRd = services.enterpriseService.useTeams(
    scopeWorkspaceNumericId != null
      ? maybe.of({
          organizationId,
          workspaceId: scopeWorkspaceNumericId
        })
      : maybe.ofAbsent()
  );
  const canManage = useManageEnterpriseFeature(services.featureService);
  const companyName = useSelector(getPublicCompanyName);
  const brandImage = useSelector(selectBrandImage);
  const account = useSelector(selectActiveAccount);
  const resourceTypesRd = services.resourceTypeService.useResourceTypes(
    rd.tryMap(activeEnterpriseRd, result => result.browsingWorkspaceScope)
  );
  const resourceTypes = rd.tryGet(resourceTypesRd) ?? [];
  const bookingTypes = useSelector(getBookingTypes);
  const productionTypes = useSelector(getProductionTypesList);
  const dispatch = useDispatch();
  const { openModal: openV2Modal } = useModal();

  const handleWorkspaceSelect = (workspaceSlug: string) => {
    history.push(orgRouting.workspace(workspaceSlug).resources.root());
  };

  const handleCreateWorkspace = () => {
    services.userFlowService.openCreateEnterpriseWorkspaceDialog(
      organizationId,
      {
        onCreated: workspace => {
          handleWorkspaceSelect(workspace.slug);
        }
      }
    );
  };

  const handleCreateTeam = () => {
    if (scopeWorkspaceNumericId == null) {
      return;
    }
    services.userFlowService.openCreateEnterpriseTeamDialog({
      organizationId,
      workspaceId: scopeWorkspaceNumericId
    });
  };

  const activeContactId = useCurrentResourceId();
  const activeResourceId = useCurrentResourceId();
  const isContactRoute = activeContactId != null;
  const isResourceRoute = activeResourceId != null;

  const to = withinApp(organizationId);

  const isOnEnterpriseResourcesRoot = useRouteMatch({
    path: resources.root(),
    exact: true
  });
  const isOnLegacyResourcesRoot = useRouteMatch({
    path: myRouting.forWorkspace(organizationId).resources().root(),
    exact: true
  });
  const isOnResourcesRoot =
    isOnEnterpriseResourcesRoot || isOnLegacyResourcesRoot;

  const isOnBookingsRoot = useRouteMatch({
    path: bookings.root(),
    exact: true
  });

  // Get keyboard shortcuts from preferences service
  const shortcuts = services.userPreferencesService.useShortcuts();

  const docusignIntegration = useSelector((state: RootStore) =>
    selectIntegrationById(state, IntegrationID.DOCUSIGN)
  );

  // Check if we're in sandbox/QA environment
  const isSandbox = !Env.isProd || Env.isQAEnvironment;

  const handleLogout = () => {
    AuthService.logout();
  };

  const handleOpenSearch = () => {
    services.globalSearchService.openSearchEvent.emit();
  };

  const handleCreateResource = (resourceTypeId: number) => {
    const enterpriseWorkspaceId =
      scope && scope.level !== 'organization' ? scope.workspaceId : undefined;
    services.userFlowService.openCreateResourceModal({
      resourceTypeId,
      ...(enterpriseWorkspaceId != null ? { enterpriseWorkspaceId } : {})
    });
  };

  const handleCreateProduction = (
    productionTypeId?: number,
    scopeOverride?: {
      enterpriseWorkspaceId?: number;
      enterpriseTeamId?: number;
    }
  ) => {
    const enterpriseWorkspaceId =
      scopeOverride?.enterpriseWorkspaceId ??
      (scope && scope.level !== 'organization' ? scope.workspaceId : undefined);
    const enterpriseTeamId =
      scopeOverride?.enterpriseTeamId ??
      (scope?.level === 'team' ? scope.teamId : undefined);
    const teamsForRedirect = rd.tryGet(teamsRd)?.map(team => ({
      id: team.id,
      slug: team.slug
    }));
    dispatch(
      dispatchOpenModal('ProductionSetUpModal', {
        create: true,
        productionTypeId,
        production: {
          ...(enterpriseWorkspaceId != null
            ? { enterprise_workspace_id: enterpriseWorkspaceId }
            : {}),
          ...(enterpriseTeamId != null
            ? { enterprise_team_id: enterpriseTeamId }
            : {})
        },
        workspaces: rd.tryGet(workspacesRd),
        teams: teamsForRedirect
      })
    );
  };

  const handleCreateShortlist = () => {
    dispatch(dispatchOpenModal('ShortlistCreateModal', { create: true }));
  };

  const handleCreateBooking = () => {
    dispatch(dispatchOpenModal('ResourceEventsModal'));
  };

  const handleCreateCallsheet = () => {
    services.userFlowService.openCreateCallsheetDialog(organizationId);
  };

  const handleCreateCallout = () => {
    const enterpriseWorkspaceId =
      scope && scope.level !== 'organization' ? scope.workspaceId : undefined;
    void services.userFlowService.openCreateCalloutModal({
      enterpriseWorkspaceId
    });
  };

  const handleCreateDocuSignRequest = () => {
    openV2Modal(() => <SplashlightDocuSignModal />);
  };

  const handleSandboxInfoClick = () => {
    dispatch(dispatchOpenModal('DevelopmentDisclaimerModal'));
  };

  const canCreateAtLeastOne = can(null, null, [
    Capability.RESOURCE_CREATE,
    Capability.PRODUCTION_CREATE,
    Capability.SHORTLIST_CREATE,
    Capability.BOOKING_CREATE,
    Capability.CALLSHEET_CREATE,
    Capability.CALLOUT_CREATE,
    Capability.DOCUSIGN_SEND
  ]);

  const shouldShowCreateResourcesGroup =
    (resourceTypes?.length ?? 0) !== 0 &&
    can(FeatureFlag.RESOURCES, Capability.RESOURCE_CREATE);

  const shouldShowCreateEntitiesGroup =
    can(FeatureFlag.PRODUCTIONS, Capability.PRODUCTION_CREATE) ||
    can(FeatureFlag.SHORTLISTS, Capability.SHORTLIST_CREATE) ||
    can(FeatureFlag.BOOKINGS, Capability.BOOKING_CREATE) ||
    can(FeatureFlag.CALLSHEETS, Capability.CALLSHEET_CREATE) ||
    can(FeatureFlag.CALLOUTS, Capability.CALLOUT_CREATE);

  function renderShortcut(shortcut: RemoteData<string>) {
    return rd.tryMap(shortcut, shortcut => (
      <Kbd className="ml-auto">
        <KeyboardShortcut shortcut={shortcut} />
      </Kbd>
    ));
  }

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex flex-col gap-2 min-h-(--header-height)">
        {headerReady ? (
          <>
            <div className="flex items-center justify-between ml-3">
              <div className="flex items-center gap-2 flex-1">
                <EnterpriseWorkspaceIcon
                  name={activeWorkspace?.name ?? 'Workspace'}
                  iconUrl={activeWorkspace?.iconUrl ?? ''}
                  className="w-8 h-8 rounded dark:border dark:border-white/20"
                />
                {isSandbox && (
                  <>
                    <Badge
                      variant="attention"
                      tone="subtle"
                      size="md"
                      className="cursor-pointer h-8"
                      onClick={handleSandboxInfoClick}
                    >
                      Sandbox
                    </Badge>
                  </>
                )}
              </div>
              <DockableSidebarToggle>
                <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
              </DockableSidebarToggle>
            </div>

            <EnterpriseWorkspaceSwitcher
              workspacesRd={workspacesRdStable}
              activeWorkspaceId={activeWorkspaceSlug}
              activeWorkspaceName={activeWorkspace?.name ?? 'Workspace'}
              activeWorkspaceIconUrl={activeWorkspace?.iconUrl ?? ''}
              organizationName={companyName ?? 'Organization'}
              organizationIconUrl={brandImage ?? ''}
              accountDisplayName={account?.displayName}
              accountProfileImageUrl={account?.profileImage}
              getWorkspacePath={workspaceSlug =>
                orgRouting.workspace(workspaceSlug).resources.root()
              }
              workspaceSettingsTo={orgRouting.settings
                .workspace(scopeWorkspaceSlug)
                .general()}
              organizationSettingsTo={orgRouting.settings.global.manageEnterprise()}
              accountDetailsTo={orgRouting.settings.personal.account()}
              onLogout={handleLogout}
              canManage={canManage}
              onCreateWorkspace={handleCreateWorkspace}
              enterpriseManagementTo={
                canManage
                  ? orgRouting.settings.global.manageEnterprise()
                  : undefined
              }
            />
          </>
        ) : (
          <EnterpriseMainSidebarHeaderSkeleton includeActions={false} />
        )}

        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 gap-4 justify-start relative pr-1.5"
            onClick={handleOpenSearch}
          >
            <Search className="size-4 ml-1" />
            <span>Search</span>
            <Kbd className="ml-auto font-normal self-stretch rounded bg-bg-subtle-2">
              <KeyboardShortcut shortcut="mod+k" />
            </Kbd>
          </Button>

          {canCreateAtLeastOne && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  <Plus className="size-4" />
                  Create
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="right"
                className="max-w-[250px]"
              >
                {shouldShowCreateResourcesGroup && (
                  <>
                    <DropdownMenuLabel>New resource</DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {resourceTypes.map(resourceType => (
                        <DropdownMenuItem
                          key={resourceType.id}
                          onClick={() => handleCreateResource(resourceType.id)}
                        >
                          <img
                            src={`/images/${RESOURCE_TYPE_ICONS[resourceType.icon]?.black}`}
                            alt={resourceType.name}
                            className="size-4 opacity-80"
                          />
                          <span className="truncate">{resourceType.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                    {shouldShowCreateEntitiesGroup && <DropdownMenuSeparator />}
                  </>
                )}

                {shouldShowCreateEntitiesGroup && (
                  <>
                    <DropdownMenuLabel>New</DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {can(
                        FeatureFlag.PRODUCTIONS,
                        Capability.PRODUCTION_CREATE
                      ) && (
                        <DropdownMenuItem
                          onClick={() => handleCreateProduction()}
                        >
                          <Camera className="size-4" />
                          Production
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.production)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(
                        FeatureFlag.SHORTLISTS,
                        Capability.SHORTLIST_CREATE
                      ) && (
                        <DropdownMenuItem onClick={handleCreateShortlist}>
                          <GalleryHorizontal className="size-4" />
                          Shortlist
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.shortlist)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(FeatureFlag.BOOKINGS, Capability.BOOKING_CREATE) && (
                        <DropdownMenuItem onClick={handleCreateBooking}>
                          <Stamp className="size-4" />
                          Booking
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.booking)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(
                        FeatureFlag.CALLSHEETS,
                        Capability.CALLSHEET_CREATE
                      ) && (
                        <DropdownMenuItem onClick={handleCreateCallsheet}>
                          <FileText className="size-4" />
                          Callsheet
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.callsheet)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(FeatureFlag.CALLOUTS, Capability.CALLOUT_CREATE) && (
                        <DropdownMenuItem onClick={handleCreateCallout}>
                          <Megaphone className="size-4" />
                          Callout
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.callout)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(
                        FeatureFlag.CUSTOM_SPLASHLIGHT,
                        Capability.DOCUSIGN_SEND,
                        null
                      ) &&
                        docusignIntegration?.enabled && (
                          <DropdownMenuItem
                            onClick={handleCreateDocuSignRequest}
                          >
                            <FileText className="size-4" />
                            Send DocuSign Request
                          </DropdownMenuItem>
                        )}
                    </DropdownMenuGroup>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <NavLink to={to.dashboard()}>
          <MenuItem icon={<Home className="size-4" />}>Dashboard</MenuItem>
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        <SidebarGroup label="Workspace">
          {can(FeatureFlag.RESOURCES, Capability.RESOURCE_READ) && (
            <>
              <MenuCollapsible
                icon={<Users className="size-4" />}
                label="Resources"
                expandOnChildSelect
                defaultOpen={isContactRoute || isResourceRoute}
              >
                <MenuItemWithCreateButton
                  level={1}
                  to={resources.root()}
                  icon={<List className="size-4" />}
                  onCreateClick={() =>
                    handleCreateResource(resourceTypes?.[0]?.id || 1)
                  }
                  createTooltip="Create new resource"
                  isActiveOverride={!!isOnResourcesRoot}
                  autoSelect
                >
                  All
                </MenuItemWithCreateButton>
                {resourceTypes?.map(resourceType => (
                  <ResourceTypeMenuItem
                    key={resourceType.id}
                    resourceType={resourceType}
                    href={resources.forResourceType(resourceType.id)}
                    onCreateClick={handleCreateResource}
                    activeContactId={activeContactId}
                  />
                ))}
              </MenuCollapsible>

              <NavLink to={calendar.resources.home()}>
                <MenuItem icon={<Calendar className="size-4" />}>
                  Resource calendar
                </MenuItem>
              </NavLink>
            </>
          )}

          {can(FeatureFlag.BOOKINGS, Capability.BOOKING_READ) && (
            <MenuCollapsible
              icon={<Stamp className="size-4" />}
              label="Bookings"
              expandOnChildSelect
            >
              <MenuItemWithCreateButton
                level={1}
                to={bookings.root()}
                icon={<List className="size-4" />}
                onCreateClick={handleCreateBooking}
                createTooltip="Create new booking"
                isActiveOverride={!!isOnBookingsRoot}
                autoSelect
              >
                All
              </MenuItemWithCreateButton>
              {bookingTypes?.map(bookingType => (
                <BookingTypeMenuItem
                  key={bookingType.id}
                  bookingType={bookingType}
                  href={bookings.type(bookingType.id)}
                  typeMatchPath={bookings.type(bookingType.id)}
                  onCreateClick={handleCreateBooking}
                />
              ))}
            </MenuCollapsible>
          )}

          {can(FeatureFlag.CALLOUTS, Capability.CALLOUT_READ) && (
            <MenuItemWithCreateButton
              to={callouts.root()}
              icon={<Megaphone className="size-4" />}
              onCreateClick={handleCreateCallout}
              createTooltip="Create new callout"
            >
              Callouts
            </MenuItemWithCreateButton>
          )}

          {can(FeatureFlag.PRODUCTIONS, Capability.PRODUCTION_READ) && (
            <NavLink to={calendar.productions.home()}>
              <MenuItem icon={<Calendar className="size-4" />}>
                Production calendar
              </MenuItem>
            </NavLink>
          )}
        </SidebarGroup>

        <EnterpriseTeamsNavSection
          teamsRd={teamsRd}
          organizationId={organizationId}
          workspaceSlug={scopeWorkspaceSlug}
          scope={scope}
          productionTypes={productionTypes}
          onCreateProduction={handleCreateProduction}
          canManage={canManage}
          onCreateTeam={handleCreateTeam}
        />

        <SidebarGroup label="Tools">
          {canManage ? (
            <NavLink to={orgRouting.settings.global.manageEnterprise()}>
              <MenuItem icon={<Building2 className="size-4" />}>
                {t('manageEnterprise')}
              </MenuItem>
            </NavLink>
          ) : null}
          {can(
            FeatureFlag.RECONCILIATIONS,
            Capability.MANAGE_RECONCILIATION
          ) && (
            <NavLink to={to.reconciliations.home()}>
              <MenuItem icon={<Stamp className="size-4" />}>
                Reconciliation
              </MenuItem>
            </NavLink>
          )}

          {can(FeatureFlag.REPORTING, Capability.REPORT_DOWNLOAD) && (
            <NavLink to={to.reporting.home()}>
              <MenuItem icon={<BarChart3 className="size-4" />}>
                Reporting
              </MenuItem>
            </NavLink>
          )}

          {can(
            FeatureFlag.NIKECUSTOM_FRONT_DESK,
            Capability.CONFIGURE_CUSTOM_NIKE_FRONT_DESK
          ) && (
            <NavLink to={to.frontdesk.home()}>
              <MenuItem icon={<BookOpenCheck className="size-4" />}>
                Front desk
              </MenuItem>
            </NavLink>
          )}
        </SidebarGroup>
      </SidebarContent>

      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}
